import { StyleSheet } from 'react-native'
import { Form, NavigationConfirm, FormProps, UploadPlaceholder, UploadDragging } from '@dms/component'
import { CommentData, DataWithUpload, Draft, PhotoData } from 'zens-store'
import { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { UploadFile, UploadFileError } from 'zens-storybook'
import { object, string } from 'yup'
import { observer } from 'mobx-react-lite'

const styles = StyleSheet.create({
  container: {
    flex: 1,
  },
})

interface Props extends Omit<FormProps, 'draft' | 'config' | 'schema' | 'buttons'> {
  companyId: number
  locationId: number
  productId?: number
  workspaceId?: number
  onSave?: (draft: Draft<DataWithUpload<CommentData | PhotoData>>) => Promise<void>
}

const FeedbackForm = (props: Props) => {
  const { companyId, locationId, productId, workspaceId, onSave, onError, style, ...otherProps } = props
  const { t } = useTranslation()
  const draft = useMemo(
    () =>
      new Draft<DataWithUpload<CommentData | PhotoData>>({
        id: NaN,
        company_id: companyId,
        ...(productId ? { product_id: productId } : undefined),
        ...(workspaceId ? { workspace_id: workspaceId } : undefined),
      }),
    [],
  )

  const updatePhoto = (value?: string, file?: File) => {
    draft.update('upload', file)
    draft.update('src', value || null)
    onError?.({})
  }

  const updateUploadError = (error: UploadFileError) => {
    switch (error) {
      case UploadFileError.FileInvalidType:
        onError?.({ src: t('Image type must be JPG, PNG or BMP') })
        break
      case UploadFileError.FileTooLarge:
        onError?.({ src: t('Image too large') })
        break
      case UploadFileError.FileTooSmall:
        onError?.({ src: t('Image too small') })
        break
      default:
        onError?.({ src: t('Image selection failed') })
    }
  }

  const config = {
    fields: [
      {
        key: 'comment',
        label: 'Comment',
        optional: true,
        multiline: true,
      },
      {
        key: 'src',
        label: t('Photo'),
        optional: true,
        FieldInputComponent: UploadFile,
        fieldProps: {
          placeholder: <UploadPlaceholder />,
          placeholderDragging: <UploadDragging />,
          deleteTitle: t('Delete image'),
          onValueChange: updatePhoto,
          onError: updateUploadError,
          progress: draft.data.progress,
        },
      },
    ],
  }

  const schema = object({
    comment: string().when('src', {
      is: (src: string | null | undefined) => !src,
      then: (schema) => schema.required(t('Remark text or photo is required')),
    }),
  })

  const buttons = [
    [
      {
        title: t('Save'),
        onPress: onSave,
        validate: true,
      },
      {
        title: t('Cancel'),
        href: {
          pathname: `/companies/${companyId}/feedback`,
          params: {
            companyId,
            locationId,
            productId,
            workspaceId,
          },
        },
        light: true,
      },
    ],
  ]
  return (
    <>
      <Form
        draft={draft}
        config={config}
        schema={schema}
        buttons={buttons}
        style={[styles.container, style]}
        {...otherProps}
      />
      <NavigationConfirm draft={draft} />
    </>
  )
}

export default observer(FeedbackForm)
